import React from 'react';
import { I18n as I } from 'react-redux-i18n';
import { array, bool, func, shape, string } from 'prop-types';
import {
  BaseButton as Button,
  FormField,
  FieldInfo,
  ControlTypes,
  isNil,
} from 'vdlib';
import { DependentDataContext } from 'components/context/DependentData';
import * as yup from 'yup';
import { useYupValidator } from 'lib/FormHelper';
import Portlet from '../layout/Portlet';
//import { validate } from './utils';

const useDependentContextPersistence = (embedded, formData, onSubmit) => {
  const { setData: setDependentData } = React.useContext(DependentDataContext);

  // Put trigger in dependent data context, if embedded in another form/modal
  React.useEffect(() => {
    if (embedded)
      setDependentData(d => ({
        ...d,
        newGarage: {
          triggerSubmit: onSubmit,
        },
      }));
  }, [embedded, onSubmit, setDependentData]);

  // Remove trigger (just trigger, no data) when component unmounts
  React.useEffect(() => {
    return () => {
      if (embedded)
        setDependentData(d => ({
          ...d,
          newGarage: {
            ...d.newGarage,
            triggerSubmit: undefined,
          },
        }));
    };
  }, []); //eslint-disable-line
};

const NewGarage = ({
  countries,
  data,
  embedded,
  lang,
  onSubmit,
  regions,
  taxOffices,
}) => {
  const [formData, setFormData] = React.useState({
    data: data || {},
    validationMessages: {},
  });
  React.useEffect(() => {
    if (data)
      setTimeout(() => setFormData(fd => ({ ...fd, data: { ...data } })));
  }, [data]);
  const formContext = React.useRef({ updateForm: () => {} });

  const fields = React.useMemo(
    () =>
      [
        new FieldInfo('DistinctiveTitle', I.t('TITLE'), ''),
        new FieldInfo('AFM', I.t('TAXNUMBER'), ''),
        new FieldInfo(
          'TaxOfficeID',
          I.t('TAXOFFICE'),
          1,
          taxOffices,
          null,
          ControlTypes.Dropdown
        ),
        new FieldInfo(
          'CountryID',
          I.t('COUNTRY'),
          67,
          countries,
          null,
          ControlTypes.Dropdown
        ),
        new FieldInfo(
          'RegionID',
          I.t('REGION'),
          1,
          regions,
          null,
          ControlTypes.Dropdown
        ),
        new FieldInfo('City', I.t('CITY'), ''),
        new FieldInfo('Street', I.t('STREET'), ''),
        new FieldInfo('StreetNumber', I.t('NUMBER'), ''),
        new FieldInfo('PostalCode', I.t('POSTALCODE'), ''),
        new FieldInfo('Phone', I.t('PHONE'), ''),
        new FieldInfo('Fax', I.t('FAX'), ''),
        new FieldInfo('Email', I.t('EMAIL'), ''),
        new FieldInfo('KAD', I.t('ACTIVΙΤΥ'), ''),
      ].map(f => ({ ...f, formContext: formContext.current })),
    [countries, formContext, lang, regions, taxOffices] //eslint-disable-line
  );

  const validationSchema = React.useMemo(() => {
    const lookupEmptyMessage = 'CHOOSE_VALUE';

    return yup.object().shape({
      AFM: yup.string().required(),
      City: yup.string().required(),
      CountryID: yup.number().positive().integer(lookupEmptyMessage),
      DistinctiveTitle: yup.string().required(),
      Email: yup.string().required(),
      KAD: yup.string().required(),
      Phone: yup.string().required(),
      PostalCode: yup.string().required(),
      RegionID: yup.number().positive().integer(lookupEmptyMessage),
      Street: yup.string().required(),
      StreetNumber: yup.string().required(),
      TaxOfficeID: yup.number().positive().integer(lookupEmptyMessage),
    });
  }, []);
  const validate = useYupValidator(validationSchema);

  const handleUpdateForm = React.useCallback(
    (key, value) => {
      const validationMessage = validate(key, value);

      setFormData(currentFormData => {
        const { data, validationMessages } = currentFormData;

        return {
          data: { ...data, [key]: value },
          validationMessages: {
            ...validationMessages,
            [key]:
              validationMessage === null ||
              isNil(validationMessage[key] ? null : validationMessage[key]),
          },
        };
      });
    },
    [validate, setFormData]
  );

  React.useEffect(() => {
    formContext.current.updateForm = handleUpdateForm;
  }, [handleUpdateForm]);

  const initialized = React.useRef(false);
  React.useEffect(() => {
    if (fields && initialized.current !== true) {
      initialized.current = true;
      const dataObj = {};
      const validationMessagesObj = {};
      for (let field of fields) {
        dataObj[field.name] = field.defaultValue;
        validationMessagesObj[field.name] = '';
      }
      setFormData({
        data: dataObj,
        validationMessages: validationMessagesObj,
      });
    }
  }, [fields, setFormData]);

  const handleSubmit = React.useCallback(
    e => {
      if (e && e.preventDefault) e.preventDefault();

      const validationResult = validate(formData.data);
      const invalidKeys = Object.keys(validationResult);

      if (invalidKeys.length === 0) onSubmit(formData.data);
      else setFormData(fd => ({ ...fd, validationMessages: validationResult }));

      return false;
    },
    [formData, onSubmit, validate]
  );

  // Put related data and trigger in context if embedded
  useDependentContextPersistence(embedded, formData, handleSubmit);

  const portletProps = {
    className: embedded === true ? 'col-md-12 pr0 pl0' : 'col-md-12',
    icon: embedded === true ? null : 'fa fa-user-plus',
    noBorder: embedded === true,
    title: embedded === true ? null : I.t('GARAGE_NEW_C'),
  };

  return (
    <Portlet bodyClassName="form" {...portletProps}>
      <form action="#" onSubmit={handleSubmit} className="form-horizontal">
        <div className="form-body clearfix">
          {embedded !== true && (
            <h3 className="form-section">{I.t('BASIC_INFO')}</h3>
          )}
          <div className="row">
            {fields.map(f => (
              <FormField
                key={f.name}
                field={f}
                value={formData.data[f.name]}
                validationMessage={formData.validationMessages[f.name]}
              />
            ))}
          </div>
        </div>
        {embedded !== true && (
          <div className="form-actions">
            <div className="row">
              <div className="col-md-12">
                <Button type="submit" className="btn btn-sm green float-right">
                  {I.t('SAVE')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </form>
    </Portlet>
  );
};

NewGarage.propTypes = {
  countries: array,
  embedded: bool,
  formContext: shape({ updateForm: func }),
  lang: string,
  onSubmit: func,
  regions: array,
  taxOffices: array,
};
export default NewGarage;
